import React, { useCallback, useEffect, useState } from 'react';

import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { WarnModal } from '@/components/elements';

import { EditServiceData } from '../../Services.types';

import { CreateServiceSuccessModal, ServiceModal } from './components';
import { useCategoryOptions, useServiceForm, useServiceSubmit } from './hooks';
import { SERVICE_MODAL_MODE } from './ServiceModal.constants';

interface Props {
  visible: boolean;
  onClose: () => void;
  editData?: EditServiceData | null;
}

const ServiceModalContainer: React.FC<Props> = ({ visible, onClose, editData }) => {
  const mode = editData ? SERVICE_MODAL_MODE.EDIT : SERVICE_MODAL_MODE.CREATE;

  const categoryOptions = useCategoryOptions();
  const { methods } = useServiceForm();

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [createdServiceName, setCreatedServiceName] = useState('');

  const handleClose = useCallback(() => {
    methods.reset();
    onClose();
  }, [methods, onClose]);

  const handleSuccess = useCallback((serviceName: string) => {
    setCreatedServiceName(serviceName);
    setIsSuccessVisible(true);
  }, []);

  const { handleSubmit, isLoading, isServerError, setServerError } = useServiceSubmit({
    mode,
    editData,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  useEffect(() => {
    if (visible) {
      if (editData) {
        methods.reset({
          name: editData.name,
          categoryType: editData.categoryType,
        });
      } else {
        methods.reset({ name: '', categoryType: '' });
      }
    }
  }, [visible, editData, methods]);

  const onSubmit = methods.handleSubmit(handleSubmit);

  const handleCloseSuccess = useCallback(() => {
    setIsSuccessVisible(false);
    setCreatedServiceName('');
    handleClose();
  }, [handleClose]);

  return (
    <>
      <ServiceModal
        visible={visible && !isSuccessVisible}
        mode={mode}
        methods={methods}
        categoryOptions={categoryOptions}
        isLoading={!!isLoading}
        onClose={handleClose}
        onSubmit={onSubmit}
      />
      <CreateServiceSuccessModal
        visible={isSuccessVisible}
        serviceName={createdServiceName}
        onClose={handleCloseSuccess}
      />
      <WarnModal
        visible={isServerError}
        onSetVisible={() => setServerError(false)}
        header={SERVER_ERROR_MODAL_TITLE}
        helptext={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        onSubmit={onSubmit}
        submitButtonText="Try Again"
      />
    </>
  );
};

export default ServiceModalContainer;
